import { Skeleton } from '@components/ui/skeleton';
import { Button } from '@components/ui/button';
import { Spinner } from '@components/ui/spinner';
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from '@components/ui/empty';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@components/ui/table';
import { listSessions } from '@generated/server/sessions';
import SessionCard from '@generated/components/SessionCard';
import { CalendarX2 } from 'lucide-react';

export type Session = Awaited<ReturnType<typeof listSessions>>['items'][number];

function lookupClockHours(name: string, map: Record<string, number | null>): string {
  const key = name.trim().toLowerCase();
  const val = map[key];
  if (val == null) return '—';
  return `${val} Hours`;
}

export default function SessionsTable({
  sessions,
  loading,
  refetching,
  loadingMore,
  hasMore,
  onLoadMore,
  clockHoursMap = {},
}: {
  sessions: Session[];
  loading: boolean;
  refetching: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  clockHoursMap?: Record<string, number | null>;
}) {
  if (loading) {
    return (
      <div className="border border-border bg-card">
        <div className="space-y-3 p-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (!sessions.length) {
    return (
      <div className="border border-border bg-card">
        <Empty className="py-16">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CalendarX2 className="size-6" aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>No sessions match these filters</EmptyTitle>
            <EmptyDescription>Try a different subject, hosting site, or date range.</EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className={`transition-opacity ${refetching ? 'opacity-60' : ''}`}>
      {/* Mobile: stacked cards */}
      <div className="flex flex-col gap-3 sm:hidden">
        {sessions.map((s) => (
          <SessionCard key={s.id} session={s} clockHours={lookupClockHours(s.name, clockHoursMap)} />
        ))}
      </div>

      {/* Desktop/tablet: table */}
      <div className="hidden border border-border bg-card sm:block">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-center">Session</TableHead>
                <TableHead className="text-center">Date &amp; Location</TableHead>
                <TableHead className="text-center">Hosting Site</TableHead>
                <TableHead className="text-center">Subject</TableHead>
                <TableHead className="text-center">Grade Band</TableHead>
                <TableHead className="text-center">Day Count</TableHead>
                <TableHead className="text-center">Clock Hours</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id} className="align-top">
                  <TableCell className="max-w-[260px] whitespace-normal break-words py-4 align-top font-medium">
                    {s.name}
                  </TableCell>
                  <TableCell className="max-w-[220px] whitespace-normal break-words py-4 align-top text-sm text-muted-foreground">
                    {s.allSessionDatesLocation || s.day1Date?.toLocaleDateString() || '—'}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words py-4 text-center align-top text-sm">
                    {s.hostingSite ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words py-4 text-center align-top text-sm">
                    {s.subjectManagedForAces ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words py-4 text-center align-top text-sm">
                    {s.gradeBandS ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words py-4 text-center align-top text-sm">
                    {s.creditHoursAsDayCount ?? '—'}
                  </TableCell>
                  <TableCell className="whitespace-normal break-words py-4 text-center align-top text-sm">
                    {lookupClockHours(s.name, clockHoursMap)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {hasMore ? (
        <div className="flex justify-center border-t border-border bg-card p-4 sm:border-t-0">
          <Button variant="outline" onClick={onLoadMore} disabled={loadingMore} className="h-11 w-full gap-2 sm:h-9 sm:w-auto">
            {loadingMore ? <Spinner className="size-4" /> : null}
            {loadingMore ? 'Loading…' : 'Load more sessions'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
